import { useEffect, useState } from 'react';
import { ChevronRight, Loader2 } from 'lucide-react';
import { GoogleBusinessInsightsSheet } from '../GoogleBusinessInsights';
import { GoogleBusinessIcon, TwilioIcon } from '../PlatformIcon';
import { useBusiness } from '../../contexts/BusinessContext';
import { supabase } from '../../lib/supabase';
import { useAggregatedGbpPerformance } from '../../hooks/useAggregatedGbpPerformance';
import { useGbpControlCentreRating } from '../../hooks/useGbpControlCentreRating';
import { useCallReports } from '../../hooks/useCallReports';
import {
  fetchCallBookingConversion,
  fetchInboundCallsByHour,
  type CallBookingConversion,
  type CallHourBucket,
} from '../../lib/callReports';
import { pctChange, type GbpPerfTotals } from '../../hooks/useGbpPerformance';
import { useLiveWebsiteVisitors } from '../../hooks/useLiveWebsiteVisitors';
import {
  emptyConversionConfig,
  loadWebsiteVisitsConversionConfig,
  saveWebsiteVisitsConversionConfig,
  type WebsiteVisitsConversionConfig,
} from '../../lib/websiteVisitsGraph';
import WebsiteVisitsConversionSettingsPanel from './WebsiteVisitsConversionSettingsPanel';
import WebsiteVisitsByMonthYtdModal from './WebsiteVisitsByMonthYtdModal';
import DevWebsiteVisitsChart from './DevWebsiteVisitsChart';

const GBP_METRICS: Array<{ key: keyof GbpPerfTotals; label: string }> = [
  { key: 'impressions', label: 'Views' },
  { key: 'calls', label: 'Calls' },
  { key: 'websiteClicks', label: 'Website' },
  { key: 'directionRequests', label: 'Directions' },
  { key: 'bookings', label: 'Bookings' },
  { key: 'conversations', label: 'Messages' },
];

/** "Oct 20" from a YYYY-MM-DD report date, without timezone drift. */
function fmtGbpDate(date: string): string {
  const [y, m, d] = date.split('-').map(Number);
  return new Date(y, m - 1, d).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

function fmtCount(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1).replace(/\.0$/, '')}M`;
  if (n >= 10_000) return `${(n / 1_000).toFixed(1).replace(/\.0$/, '')}K`;
  return n.toLocaleString();
}

function GoogleReviewSummary({
  count,
  rating,
  loading,
}: {
  count: number | null;
  rating: number | null;
  loading: boolean;
}) {
  if (loading) {
    return <Loader2 className="h-3.5 w-3.5 animate-spin text-gray-300" aria-label="Loading Google reviews" />;
  }
  if (count == null && rating == null) return null;
  const reviewLabel = count === 1 ? 'review' : 'reviews';
  return (
    <span
      className="inline-flex items-baseline gap-1 text-[12px] font-semibold tabular-nums text-gray-900"
      title="Star rating is the location's overall Google rating. The count is reviews received this month."
    >
      {rating != null ? (
        <span>
          {rating.toFixed(1)}
          <span className="text-amber-500">★</span>
        </span>
      ) : null}
      {rating != null && count != null ? <span className="font-normal text-gray-300">·</span> : null}
      {count != null ? (
        <span>
          {fmtCount(count)} <span className="font-normal text-gray-500">{reviewLabel}</span>
        </span>
      ) : null}
    </span>
  );
}

function Delta({ value, invert = false }: { value: number | null; invert?: boolean }) {
  if (value == null) return null;
  const rounded = Math.round(Math.abs(value));
  if (rounded === 0) return null;
  const up = value > 0;
  const good = invert ? !up : up;
  return (
    <span className={`text-[10px] font-semibold shrink-0 ${good ? 'text-green-600' : 'text-red-500'}`}>
      {up ? '▲' : '▼'}
      {rounded}%
    </span>
  );
}

function answerRate(answered: number, missed: number): number | null {
  const total = answered + missed;
  if (total === 0) return null;
  return (answered / total) * 100;
}

function hourLabel(hour: number): string {
  if (hour === 0 || hour === 24) return '12a';
  if (hour === 12) return '12p';
  if (hour < 12) return `${hour}a`;
  return `${hour - 12}p`;
}

function hourTotal(bucket: CallHourBucket): number {
  return bucket.answered + bucket.missed + bucket.other;
}

/**
 * The stretch of day where calls actually cluster. A single overnight call
 * does not stretch the chart across the whole day.
 */
function busyHourWindow(buckets: CallHourBucket[]): CallHourBucket[] {
  const clusters: { start: number; end: number; total: number }[] = [];
  let current: { start: number; end: number; total: number } | null = null;
  let gap = 0;
  buckets.forEach((bucket) => {
    const count = hourTotal(bucket);
    if (count > 0) {
      if (!current || gap > 1) {
        if (current) clusters.push(current);
        current = { start: bucket.hour, end: bucket.hour, total: count };
      } else {
        current.end = bucket.hour;
        current.total += count;
      }
      gap = 0;
    } else if (current) {
      gap += 1;
    }
  });
  if (current) clusters.push(current);
  if (clusters.length === 0) return buckets.slice(8, 18);

  const main = clusters.reduce((best, cluster) => (cluster.total > best.total ? cluster : best));
  let start = Math.max(0, main.start - 1);
  let end = Math.min(23, main.end + 1);
  if (end - start < 8) {
    end = Math.min(23, start + 8);
    start = Math.max(0, end - 8);
  }
  return buckets.slice(start, end + 1);
}

function CallStat({
  label,
  value,
  delta,
  invert = false,
}: {
  label: string;
  value: string;
  delta: number | null;
  invert?: boolean;
}) {
  return (
    <div className="min-w-0 rounded-xl border border-gray-200 bg-white px-2.5 py-2 shadow-[0_1px_2px_rgba(0,0,0,0.04)]">
      <div className="flex min-w-0 items-baseline gap-1">
        <span className="truncate text-[15px] font-bold tabular-nums leading-tight text-gray-900">
          {value}
        </span>
        <Delta value={delta} invert={invert} />
      </div>
      <p className="mt-0.5 truncate text-[10px] text-gray-500">{label}</p>
    </div>
  );
}

function CallBookingCompare({
  bookings,
  previousBookings,
  calls,
}: {
  bookings: number;
  previousBookings: number;
  calls: number;
}) {
  const share = calls > 0 ? (bookings / calls) * 100 : null;

  return (
    <div className="mt-3">
      <div className="flex items-baseline justify-between gap-2">
        <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-400">
          Jobs from calls
        </p>
        <Delta value={pctChange(bookings, previousBookings)} />
      </div>
      <div className="mt-1 flex items-baseline justify-between gap-3">
        <p className="text-[15px] font-bold tabular-nums text-gray-900">
          {fmtCount(bookings)}
          <span className="ml-1.5 text-[12px] font-medium text-gray-500">
            {bookings === 1 ? 'job' : 'jobs'}
          </span>
        </p>
        <p className="shrink-0 text-[12px] tabular-nums text-gray-500">{fmtCount(calls)} calls</p>
      </div>
      <div className="mt-1.5 h-1.5 overflow-hidden rounded-full bg-gray-100">
        <div
          className="h-full rounded-full bg-[#1a73e8]"
          style={{ width: `${share == null ? 0 : Math.min(100, share)}%` }}
        />
      </div>
      <p className="mt-1 text-[11px] text-gray-500">
        {share == null ? 'No calls this month' : `${Math.round(share)}% of calls`}
      </p>
    </div>
  );
}

function TwilioCallsPanel({
  loading,
  total,
  totalPrevious,
  answered,
  answeredPrevious,
  missed,
  missedPrevious,
  hours,
  booked,
}: {
  loading: boolean;
  total: number;
  totalPrevious: number;
  answered: number;
  answeredPrevious: number;
  missed: number;
  missedPrevious: number;
  hours: CallHourBucket[] | null;
  booked: CallBookingConversion | null;
}) {
  const rate = answerRate(answered, missed);
  const previousRate = answerRate(answeredPrevious, missedPrevious);
  const windowHours = hours ? busyHourWindow(hours) : [];
  const maxHour = Math.max(1, ...windowHours.map(hourTotal));
  const hasOther = windowHours.some((bucket) => bucket.other > 0);
  const labelEvery = windowHours.length > 12 ? 2 : 1;

  return (
    <div data-cc-inset className="p-3 sm:p-4">
      <div className="mb-3 flex min-w-0 items-center gap-2.5">
        <TwilioIcon className="h-8 w-8 shrink-0" />
        <div className="min-w-0">
          <p className="truncate text-[14px] font-semibold text-gray-900">Twilio</p>
          <p className="truncate text-[11px] text-gray-500">This month · inbound calls</p>
        </div>
      </div>
      {loading ? (
        <div className="flex items-center gap-2 py-6 text-[13px] text-gray-500">
          <Loader2 className="h-4 w-4 animate-spin" />
          Loading calls…
        </div>
      ) : (
        <>
          <div className="grid grid-cols-2 gap-1.5 sm:grid-cols-4 sm:gap-2">
            <CallStat
              label="Total calls"
              value={fmtCount(total)}
              delta={pctChange(total, totalPrevious)}
            />
            <CallStat
              label="Answered"
              value={fmtCount(answered)}
              delta={pctChange(answered, answeredPrevious)}
            />
            <CallStat
              label="Missed"
              value={fmtCount(missed)}
              delta={pctChange(missed, missedPrevious)}
              invert
            />
            <CallStat
              label="Answer rate"
              value={rate == null ? '—' : `${Math.round(rate)}%`}
              delta={rate != null && previousRate != null ? pctChange(rate, previousRate) : null}
            />
          </div>

          {booked ? (
            <CallBookingCompare
              bookings={booked.bookings}
              previousBookings={booked.previousBookings}
              calls={total}
            />
          ) : null}

          <div className="mt-4">
            <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-400">
              Busy times
            </p>
            {windowHours.every((bucket) => hourTotal(bucket) === 0) ? (
              <p className="mt-2 text-[12px] text-gray-500">No inbound calls this month yet.</p>
            ) : (
              <>
                <div className="mt-2 flex h-24 items-end gap-[3px]">
                  {windowHours.map((bucket) => {
                    const totalCalls = hourTotal(bucket);
                    const scale = (totalCalls / maxHour) * 100;
                    return (
                      <div
                        key={bucket.hour}
                        className="flex h-full min-w-0 flex-1 flex-col justify-end"
                        title={`${hourLabel(bucket.hour)} · ${totalCalls} call${totalCalls === 1 ? '' : 's'}`}
                      >
                        <div
                          className="flex w-full flex-col justify-end overflow-hidden rounded-sm"
                          style={{ height: `${scale}%`, minHeight: totalCalls > 0 ? 3 : 0 }}
                        >
                          {bucket.other > 0 ? (
                            <div
                              className="w-full bg-gray-300"
                              style={{ flex: bucket.other }}
                            />
                          ) : null}
                          {bucket.missed > 0 ? (
                            <div className="w-full bg-red-400" style={{ flex: bucket.missed }} />
                          ) : null}
                          {bucket.answered > 0 ? (
                            <div
                              className="w-full bg-[#1a73e8]"
                              style={{ flex: bucket.answered }}
                            />
                          ) : null}
                        </div>
                      </div>
                    );
                  })}
                </div>
                <div className="mt-1 flex gap-[3px]">
                  {windowHours.map((bucket, index) => (
                    <span
                      key={bucket.hour}
                      className="min-w-0 flex-1 truncate text-center text-[9px] text-gray-400"
                    >
                      {index % labelEvery === 0 ? hourLabel(bucket.hour) : ''}
                    </span>
                  ))}
                </div>
                <div className="mt-2 flex flex-wrap items-center gap-3 text-[10px] text-gray-500">
                  <span className="inline-flex items-center gap-1">
                    <span className="h-2 w-2 rounded-sm bg-[#1a73e8]" />
                    Answered
                  </span>
                  <span className="inline-flex items-center gap-1">
                    <span className="h-2 w-2 rounded-sm bg-red-400" />
                    Missed
                  </span>
                  {hasOther ? (
                    <span className="inline-flex items-center gap-1">
                      <span className="h-2 w-2 rounded-sm bg-gray-300" />
                      Other
                    </span>
                  ) : null}
                </div>
              </>
            )}
          </div>
        </>
      )}
    </div>
  );
}

/**
 * Control Centre performance: compiled Google Business totals (expand into the
 * marketing insights modal) and the website visits chart, plus a
 * live visitor count.
 */
export default function ControlCentreAcquisitionPanel() {
  const { selectedBusiness } = useBusiness();
  const businessId = selectedBusiness?.id;
  const hasHostedWebsite = Boolean(
    (selectedBusiness as { vercel_project_id?: string | null } | null)?.vercel_project_id
      ?.toString()
      .trim(),
  );
  const [trackingEnabled, setTrackingEnabled] = useState(false);
  const [trackingLoaded, setTrackingLoaded] = useState(false);
  const hasWebsite = hasHostedWebsite || trackingEnabled;
  const hasTwilio = Boolean(
    (selectedBusiness as { twilio_enabled?: boolean | null } | null)?.twilio_enabled,
  );

  const timezone = selectedBusiness?.timezone || 'UTC';
  const gbp = useAggregatedGbpPerformance(businessId, timezone);
  const googleReviews = useGbpControlCentreRating();
  const calls = useCallReports('this_month');
  const [callHours, setCallHours] = useState<CallHourBucket[] | null>(null);
  const [callBookings, setCallBookings] = useState<CallBookingConversion | null>(null);
  const [conversionSettingsOpen, setConversionSettingsOpen] = useState(false);
  const [visitsByMonthOpen, setVisitsByMonthOpen] = useState(false);
  const [conversionConfig, setConversionConfig] =
    useState<WebsiteVisitsConversionConfig>(emptyConversionConfig());
  const [chartRevision, setChartRevision] = useState(0);
  const [insightsTarget, setInsightsTarget] = useState<string | 'all' | null>(null);
  const [locationMenuOpen, setLocationMenuOpen] = useState(false);

  const live = useLiveWebsiteVisitors(businessId, hasWebsite);

  useEffect(() => {
    if (!businessId) {
      setTrackingEnabled(false);
      setTrackingLoaded(true);
      return;
    }
    let cancelled = false;
    setTrackingLoaded(false);
    void supabase
      .from('business_analytics_config')
      .select('is_active')
      .eq('business_id', businessId)
      .maybeSingle()
      .then(({ data }) => {
        if (cancelled) return;
        setTrackingEnabled(data?.is_active === true);
        setTrackingLoaded(true);
      })
      .catch(() => {
        if (cancelled) return;
        setTrackingEnabled(false);
        setTrackingLoaded(true);
      });
    return () => {
      cancelled = true;
    };
  }, [businessId]);

  useEffect(() => {
    if (!businessId) {
      setConversionConfig(emptyConversionConfig());
      return;
    }
    let cancelled = false;
    void loadWebsiteVisitsConversionConfig(businessId).then((config) => {
      if (!cancelled) setConversionConfig(config);
    });
    return () => {
      cancelled = true;
    };
  }, [businessId]);

  useEffect(() => {
    if (!hasTwilio || !businessId) {
      setCallHours(null);
      setCallBookings(null);
      return;
    }
    let cancelled = false;
    setCallHours(null);
    setCallBookings(null);
    void fetchInboundCallsByHour(businessId, timezone)
      .then((rows) => {
        if (!cancelled) setCallHours(rows);
      })
      .catch(() => {
        if (!cancelled) setCallHours([]);
      });
    void fetchCallBookingConversion(businessId, 'this_month', timezone)
      .then((row) => {
        if (!cancelled) setCallBookings(row);
      })
      .catch(() => {
        if (!cancelled) setCallBookings({ bookings: 0, previousBookings: 0 });
      });
    return () => {
      cancelled = true;
    };
  }, [hasTwilio, businessId, timezone]);

  if (!hasWebsite && !hasTwilio && (gbp.loading || !trackingLoaded)) return null;
  if (!hasWebsite && !hasTwilio && !gbp.connected) return null;

  const insightsLocation =
    insightsTarget && insightsTarget !== 'all'
      ? gbp.directLocations.find((location) => location.id === insightsTarget) ?? null
      : null;
  const insightsConnectionIds =
    insightsTarget === 'all'
      ? gbp.directLocations.map((location) => location.id)
      : insightsLocation
        ? [insightsLocation.id]
        : [];

  const openInsights = () => {
    if (gbp.directLocations.length <= 1) {
      setInsightsTarget(gbp.directLocations[0]?.id ?? null);
      setLocationMenuOpen(false);
      return;
    }
    setLocationMenuOpen((open) => !open);
  };

  return (
    <section
      className="mb-5"
      data-control-centre-panel
      aria-label="Website and Google Business performance"
    >
      <header className="px-4 py-3 sm:px-5">
        <h2 className="text-[15px] font-semibold text-gray-900">Performance</h2>
      </header>

      <div className="control-centre-panel__body space-y-3">
        {gbp.connected ? (
          <div data-cc-inset className="p-3 sm:p-4">
            <div className="mb-3 flex items-center justify-between gap-3">
              <div className="flex min-w-0 items-center gap-2.5">
                <GoogleBusinessIcon className="h-8 w-8 shrink-0" />
                <div className="min-w-0">
                  <div className="flex min-w-0 flex-wrap items-baseline gap-x-2">
                    <p className="text-[14px] font-semibold text-gray-900">Google Business</p>
                    <GoogleReviewSummary
                      count={googleReviews.reviewCount}
                      rating={googleReviews.rating}
                      loading={googleReviews.ratingLoading}
                    />
                  </div>
                  <p className="truncate text-[11px] text-gray-500">
                    This month
                    {gbp.locationCount > 1 ? ' · all locations' : ''}
                    {gbp.dataThrough ? ` · through ${fmtGbpDate(gbp.dataThrough)}` : ''}
                  </p>
                </div>
              </div>
              {gbp.directLocations.length > 0 ? (
                <div className="relative shrink-0">
                  <button
                    type="button"
                    onClick={openInsights}
                    className="inline-flex items-center gap-0.5 text-[12px] font-semibold text-[#1a73e8] hover:underline"
                  >
                    Full insights
                    <ChevronRight className="h-3.5 w-3.5" />
                  </button>
                  {locationMenuOpen && gbp.directLocations.length > 1 ? (
                    <div className="absolute right-0 z-20 mt-1 w-56 overflow-hidden rounded-xl border border-gray-200 bg-white py-1 shadow-lg">
                      <button
                        type="button"
                        onClick={() => {
                          setInsightsTarget('all');
                          setLocationMenuOpen(false);
                        }}
                        className="block w-full truncate px-3 py-2 text-left text-[13px] font-semibold text-gray-900 hover:bg-gray-50"
                      >
                        All businesses
                      </button>
                      {gbp.directLocations.map((location) => (
                        <button
                          key={location.id}
                          type="button"
                          onClick={() => {
                            setInsightsTarget(location.id);
                            setLocationMenuOpen(false);
                          }}
                          className="block w-full truncate px-3 py-2 text-left text-[13px] text-gray-800 hover:bg-gray-50"
                        >
                          {location.name || 'Google Business'}
                        </button>
                      ))}
                    </div>
                  ) : null}
                </div>
              ) : null}
            </div>

            {gbp.loading ? (
              <div className="flex items-center gap-2 py-6 text-[13px] text-gray-500">
                <Loader2 className="h-4 w-4 animate-spin" />
                Loading Google insights…
              </div>
            ) : gbp.totals ? (
              <div className="grid grid-cols-3 gap-1.5 sm:gap-2">
                {GBP_METRICS.map((metric) => (
                  <div
                    key={metric.key}
                    className="min-w-0 rounded-xl border border-gray-200 bg-white px-2.5 py-2 shadow-[0_1px_2px_rgba(0,0,0,0.04)]"
                  >
                    <div className="flex min-w-0 items-baseline gap-1">
                      <span className="truncate text-[15px] font-bold tabular-nums leading-tight text-gray-900">
                        {fmtCount(gbp.totals![metric.key])}
                      </span>
                      <Delta
                        value={pctChange(gbp.totals![metric.key], gbp.previousTotals?.[metric.key])}
                      />
                    </div>
                    <p className="mt-0.5 truncate text-[10px] text-gray-500">{metric.label}</p>
                  </div>
                ))}
              </div>
            ) : (
              <div>
                <p className="text-[13px] text-gray-500">
                  {gbp.unsupported
                    ? 'Performance isn’t available for this connection.'
                    : 'Couldn’t load Google performance.'}
                </p>
              </div>
            )}
          </div>
        ) : null}

        {hasWebsite ? (
          <div data-cc-inset className="p-3 sm:p-4">
            <div className="mb-3 flex min-w-0 items-center justify-between gap-3">
              <div className="flex min-w-0 items-center gap-2">
                <p className="text-[14px] font-semibold text-gray-900">Website</p>
                <span className="inline-flex items-center gap-1.5 text-[12px] text-gray-500">
                  <span
                    className={`h-2 w-2 rounded-full ${live.count > 0 ? 'bg-green-500' : 'bg-gray-300'}`}
                  />
                  {live.loading ? (
                    <Loader2 className="h-3.5 w-3.5 animate-spin text-gray-300" />
                  ) : (
                    <span>
                      Live visitors{' '}
                      <span className="font-semibold tabular-nums text-gray-900">{live.count}</span>
                    </span>
                  )}
                </span>
              </div>
              <p className="shrink-0 text-[11px] text-gray-500">This month</p>
            </div>

            {live.latest ? (
              <p className="mb-3 truncate text-[12px] text-gray-500">
                {live.latest.path} · Page view
                {live.latest.timeLabel ? ` · ${live.latest.timeLabel}` : ''}
              </p>
            ) : null}

            <DevWebsiteVisitsChart
              revision={chartRevision}
              onByMonth={() => setVisitsByMonthOpen(true)}
              onSettings={() => setConversionSettingsOpen(true)}
            />
          </div>
        ) : null}

        {hasTwilio ? (
          <TwilioCallsPanel
            loading={calls.loading || callHours == null || callBookings == null}
            total={calls.phoneCalls.count}
            totalPrevious={calls.phoneCalls.previousCount}
            answered={calls.answeredCalls.count}
            answeredPrevious={calls.answeredCalls.previousCount}
            missed={calls.missedCalls.count}
            missedPrevious={calls.missedCalls.previousCount}
            hours={callHours}
            booked={callBookings}
          />
        ) : null}
      </div>

      {businessId && insightsConnectionIds.length > 0 ? (
        <GoogleBusinessInsightsSheet
          businessId={businessId}
          connectionIds={insightsConnectionIds}
          locationName={insightsTarget === 'all' ? 'All businesses' : insightsLocation?.name}
          open
          onClose={() => setInsightsTarget(null)}
        />
      ) : null}

      <WebsiteVisitsConversionSettingsPanel
        open={conversionSettingsOpen}
        businessId={businessId}
        period="this_month"
        initialConfig={conversionConfig}
        onClose={(next) => {
          setConversionSettingsOpen(false);
          if (!businessId) return;
          const cleaned: WebsiteVisitsConversionConfig = {
            enabled: next.enabled,
            criteria: next.criteria
              .filter((criterion) => criterion.value)
              .map((criterion) => ({
                type: criterion.type,
                value: criterion.value,
                ...(criterion.conversionTypeId ? { conversionTypeId: criterion.conversionTypeId } : {}),
              })),
            customTypes: (next.customTypes || []).filter((type) => type.id.trim() && type.label.trim()),
          };
          void saveWebsiteVisitsConversionConfig(businessId, cleaned).then(() => {
            setConversionConfig(cleaned);
            setChartRevision((revision) => revision + 1);
          });
        }}
      />
      <WebsiteVisitsByMonthYtdModal
        isOpen={visitsByMonthOpen}
        onClose={() => setVisitsByMonthOpen(false)}
      />
    </section>
  );
}
